import { useEffect, useState } from "react";
import axios from "axios";
import { Download, Lock, LogOut, Trash2 } from "lucide-react";
import { LaurelMark } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "lov-admin-key";

export default function Admin() {
  const [key, setKey] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [unlocked, setUnlocked] = useState(false);
  const [leads, setLeads] = useState([]);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const load = async (value) => {
    try {
      const res = await axios.get(`${API}/admin/leads`, { headers: { "X-Admin-Key": value } });
      setLeads(res.data);
      setUnlocked(true);
      setError("");
    } catch {
      sessionStorage.removeItem(STORAGE_KEY);
      setUnlocked(false);
      setError("Invalid passcode.");
    }
  };

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unlock = (e) => {
    e.preventDefault();
    sessionStorage.setItem(STORAGE_KEY, key);
    load(key);
  };

  const lock = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setKey("");
    setUnlocked(false);
    setLeads([]);
  };

  const referrerName = (lead) => {
    if (!lead.referred_by) return "Direct";
    const ref = leads.find((x) => x.id === lead.referred_by);
    return ref ? `${ref.name} (shared link)` : "Shared link";
  };

  const deleteLead = async (lead) => {
    if (!window.confirm(`Remove ${lead.name} (${lead.email}) from your leads?`)) return;
    try {
      await axios.delete(`${API}/admin/leads/${lead.id}`, {
        headers: { "X-Admin-Key": sessionStorage.getItem(STORAGE_KEY) },
      });
      setLeads((prev) => prev.filter((x) => x.id !== lead.id));
    } catch {
      setError("Could not remove that lead. Please try again.");
    }
  };

  const exportCsv = () => {
    const header = ["Name", "Email", "Date", "Overall", "Strongest Area", "Primary Gap", "Wants Support", "Share Views", "Referred By"];
    const rows = leads.map((l) =>
      [l.name, l.email, l.audit_date, l.overall_result, l.strongest_area, l.primary_gap, l.wants_support, l.share_views ?? 0, referrerName(l)]
        .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
        .join(",")
    );
    const blob = new Blob([[header.join(","), ...rows].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "lady-of-victory-leads.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const filtered = leads.filter(
    (l) =>
      l.name.toLowerCase().includes(search.toLowerCase()) ||
      l.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-ivory" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex items-center gap-3 mb-10">
          <LaurelMark className="w-8 h-8" />
          <h1 className="font-serif text-3xl text-burgundy-deep">Alignment Leads</h1>
        </div>

        {!unlocked ? (
          <form onSubmit={unlock} className="max-w-sm grid gap-4" data-testid="admin-unlock-form">
            <label className="text-xs uppercase tracking-[0.25em] text-gold-muted" htmlFor="admin-key">
              Admin passcode
            </label>
            <input
              id="admin-key"
              data-testid="admin-passcode-input"
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              className="border border-burgundy/25 bg-white px-5 py-3 focus:outline-none focus:border-burgundy transition-colors"
              autoComplete="off"
            />
            {error && <p className="text-sm text-red-700" data-testid="admin-error-message">{error}</p>}
            <button
              data-testid="admin-unlock-btn"
              type="submit"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-burgundy text-champagne px-6 py-3 text-sm tracking-widest uppercase hover:bg-burgundy-deep transition-colors"
            >
              <Lock className="w-4 h-4" /> Unlock
            </button>
          </form>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-4 mb-8">
              <input
                data-testid="admin-search-input"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or email…"
                className="border border-burgundy/25 bg-white px-5 py-2.5 w-72 focus:outline-none focus:border-burgundy transition-colors"
              />
              <span className="text-sm text-[#62686E]" data-testid="admin-lead-count">
                {filtered.length} lead{filtered.length === 1 ? "" : "s"}
              </span>
              <div className="ml-auto flex gap-3">
                <button
                  data-testid="admin-export-csv-btn"
                  onClick={exportCsv}
                  className="inline-flex items-center gap-2 rounded-full border border-burgundy/30 px-5 py-2.5 text-xs uppercase tracking-widest text-burgundy hover:bg-burgundy hover:text-champagne transition-colors"
                >
                  <Download className="w-4 h-4" /> Export CSV
                </button>
                <button
                  data-testid="admin-lock-btn"
                  onClick={lock}
                  className="inline-flex items-center gap-2 rounded-full border border-burgundy/30 px-5 py-2.5 text-xs uppercase tracking-widest text-burgundy hover:bg-burgundy hover:text-champagne transition-colors"
                >
                  <LogOut className="w-4 h-4" /> Lock
                </button>
              </div>
            </div>

            <div className="overflow-x-auto border border-burgundy/15 bg-white" data-testid="admin-leads-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#EEF0F2] text-left text-xs uppercase tracking-widest text-burgundy">
                    <th className="px-5 py-4">Name</th>
                    <th className="px-5 py-4">Email</th>
                    <th className="px-5 py-4">Date</th>
                    <th className="px-5 py-4">Overall</th>
                    <th className="px-5 py-4">Strongest</th>
                    <th className="px-5 py-4">Gap</th>
                    <th className="px-5 py-4">Wants Support</th>
                    <th className="px-5 py-4">Share Views</th>
                    <th className="px-5 py-4">Referred By</th>
                    <th className="px-5 py-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l) => (
                    <tr key={l.id} className="border-t border-burgundy/10 hover:bg-ivory/60" data-testid={`admin-lead-row-${l.id}`}>
                      <td className="px-5 py-4 font-medium text-burgundy-deep">{l.name}</td>
                      <td className="px-5 py-4 text-[#62686E]">{l.email}</td>
                      <td className="px-5 py-4 text-[#62686E]">{new Date(l.audit_date).toLocaleDateString()}</td>
                      <td className="px-5 py-4">{l.overall_result}</td>
                      <td className="px-5 py-4">{l.strongest_area}</td>
                      <td className="px-5 py-4">{l.primary_gap}</td>
                      <td className="px-5 py-4">{l.wants_support}</td>
                      <td className="px-5 py-4" data-testid={`admin-share-views-${l.id}`}>{l.share_views ?? 0}</td>
                      <td className="px-5 py-4 text-[#62686E]">{referrerName(l)}</td>
                      <td className="px-5 py-4">
                        <button
                          data-testid={`admin-delete-lead-${l.id}`}
                          onClick={() => deleteLead(l)}
                          className="text-[#62686E] hover:text-red-700 transition-colors"
                          title="Remove lead"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={9} className="px-5 py-10 text-center text-[#62686E]" data-testid="admin-empty-state">
                        No leads yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
